import React, { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import {
  renderBarcodeSvg,
  LABEL_BARCODE_SVG_MAX_HEIGHT_MM,
  LABEL_BARCODE_PREVIEW_JS_OPTIONS,
} from '../../utils/inventoryLabelBarcode';
import {
  LABEL_PRINT_WIDTH_MM,
  LABEL_PRINT_HEIGHT_MM,
  LABEL_INFO_LEFT_MM,
  LABEL_BARCODE_LEFT_MM,
  LABEL_INFO_WIDTH_MM,
  LABEL_BARCODE_WIDTH_MM,
} from '../../utils/printInventoryLabels';

/** Campos mínimos para renderizar la etiqueta 107×11 mm (misma geometría absoluta que impresión). */
export interface InventoryLabelPreviewRow {
  productCodeUnique: string;
  materialLabel: string;
  salePrice: number;
  weightGrams: number | null;
}

export function InventoryLabelPreviewMini({ row }: { row: InventoryLabelPreviewRow }) {
  const svgRef = useRef<SVGSVGElement>(null);
  useEffect(() => {
    renderBarcodeSvg(row.productCodeUnique ? svgRef.current : null, row.productCodeUnique ?? '', {
      ...LABEL_BARCODE_PREVIEW_JS_OPTIONS,
    });
  }, [row.productCodeUnique]);

  const priceStr = `Bs. ${Number(row.salePrice).toLocaleString('es-BO', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
  const w =
    row.weightGrams != null && !Number.isNaN(Number(row.weightGrams))
      ? Number(row.weightGrams).toLocaleString('es-BO', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        })
      : '—';

  return (
    <Box
      sx={{
        position: 'relative',
        width: `${LABEL_PRINT_WIDTH_MM}mm`,
        height: `${LABEL_PRINT_HEIGHT_MM}mm`,
        overflow: 'hidden',
        bgcolor: '#fff',
        color: '#111827',
        fontFamily: '"Times New Roman", Times, serif',
        flexShrink: 0,
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          left: `${LABEL_INFO_LEFT_MM}mm`,
          top: 0,
          width: `${LABEL_INFO_WIDTH_MM}mm`,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        <Typography sx={{ fontStyle: 'italic', fontSize: '10pt', lineHeight: 1 }} noWrap>
          {row.materialLabel}
        </Typography>
        <Typography sx={{ fontSize: '10pt', lineHeight: 1 }} noWrap>
          {priceStr}
        </Typography>
      </Box>
      <Box
        sx={{
          position: 'absolute',
          left: `${LABEL_BARCODE_LEFT_MM}mm`,
          top: 0,
          width: `${LABEL_BARCODE_WIDTH_MM}mm`,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: '0.9mm',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            flex: '1 1 auto',
            minHeight: 0,
            maxHeight: `${LABEL_BARCODE_SVG_MAX_HEIGHT_MM}mm`,
            width: '100%',
            overflow: 'hidden',
          }}
        >
          <svg
            ref={svgRef}
            preserveAspectRatio="xMidYMid meet"
            style={{
              width: 'auto',
              maxWidth: '100%',
              height: `${LABEL_BARCODE_SVG_MAX_HEIGHT_MM}mm`,
              maxHeight: `${LABEL_BARCODE_SVG_MAX_HEIGHT_MM}mm`,
              display: 'block',
            }}
          />
        </Box>
        <Box
          sx={{
            flex: '0 0 auto',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            width: '100%',
            fontSize: '10pt',
            lineHeight: 1,
            overflow: 'hidden',
          }}
        >
          <span>{w}</span>
          <span style={{ textDecoration: 'underline' }}>{row.productCodeUnique}</span>
        </Box>
      </Box>
    </Box>
  );
}
